import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveCompetition } from '../../hooks/useActiveCompetition';
import { describeLifecycle, deriveLifecycle } from '@shared/competitionRules';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import LoginForm from '../auth/LoginForm';
import CheckInModal from '../member/CheckInModal';
import NonMemberRegistrationForm from './NonMemberRegistrationForm';
import { formatClubDate, formatClubTime } from '../../utils/clubTime';

// userPool — getOwnMemberProfile is authenticated-only; this page must never
// look up a member by a caller-supplied identifier (see
// .kiro/specs/competition-checkin-security-fix/). The member/family data
// used by CheckInModal always comes from the caller's own resolved identity.
const memberClient = generateClient({ authMode: 'userPool' });
// apiKey — full Event record fetch, guest registration. Runs before any
// sign-in decision is made, same as useActiveCompetition's own client.
const publicClient = generateClient({ authMode: 'apiKey' });

type Flow = 'choice' | 'signing-in' | 'loading-profile' | 'checking-in' | 'guest';

/**
 * After a successful check-in, Done never returns to /check-in (user decision,
 * 5 Oct 2026, #109): a member goes to their dashboard, a guest to the
 * homepage. Same pushState + popstate pattern as LandingPage.
 */
function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The single check-in entry point for both members and guests — replaces
 * the previous split between this page (behind the permanent /check-in QR)
 * and CheckInPage.tsx (behind /competition-check-in/:eventId). Consolidated
 * 15 Aug 2026 after the two pages drifted into inconsistent UX (button vs.
 * tile styling) and CheckInPage.tsx linked members out to a full /dashboard
 * redirect instead of the in-place login+check-in flow built here. One
 * page, one URL, one visual pattern. See
 * .kiro/specs/competition-checkin-security-fix/.
 */
const SmartCheckInPage: React.FC = () => {
  const { user, isLoading: authLoading, isMember, refreshAuth } = useAuth();
  const { loading, openComps, activeEventId, nextComp, setActiveEventId } = useActiveCompetition({ purpose: 'checkIn' });

  const [flow, setFlow] = useState<Flow>('choice');
  const [member, setMember] = useState<any>(null);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [event, setEvent] = useState<any>(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState('');
  const [checkInWindow, setCheckInWindow] = useState({ isOpen: false, message: '' });

  // Full Event record — needed for guest registration pricing/capacity
  // display, which useActiveCompetition's shared, minimal shape doesn't carry.
  useEffect(() => {
    if (!activeEventId) return;
    setEventLoading(true);
    setEventError('');
    publicClient.models.Event.get({ id: activeEventId })
      .then((result: any) => {
        if (!result?.data) {
          setEventError('Event not found.');
          return;
        }
        setEvent(result.data);
      })
      .catch(() => setEventError('Failed to load event details. Please try again.'))
      .finally(() => setEventLoading(false));
  }, [activeEventId]);

  useEffect(() => {
    if (!event) return;
    const compute = () => {
      const now = new Date();
      const l = deriveLifecycle(event, now);
      const { signOn } = describeLifecycle(event, now, formatClubTime);
      setCheckInWindow({ isOpen: l.signOnOpen, message: signOn });
    };
    compute();
    const interval = setInterval(compute, 60000);
    return () => clearInterval(interval);
  }, [event]);

  const loadOwnProfileAndCheckIn = async () => {
    setFlow('loading-profile');
    setProfileError(null);
    try {
      const result: any = await (memberClient.queries as any).getOwnMemberProfile({});
      const response = parseLambdaResponse(result?.data);
      if (!response?.success || !response.own) {
        setProfileError(
          response?.error === 'Member profile not found'
            ? "We couldn't find a membership linked to your account. Please see an admin at the sign-on desk."
            : 'Failed to load your membership details. Please try again, or see an admin.'
        );
        setFlow('choice');
        return;
      }
      setMember(response.own);
      setFamilyMembers(response.family || []);
      setFlow('checking-in');
    } catch {
      setProfileError('Failed to load your membership details. Please try again, or see an admin.');
      setFlow('choice');
    }
  };

  // An already-authenticated member goes straight to their own check-in on
  // THIS screen — no redirect to /dashboard, no separate page.
  useEffect(() => {
    if (authLoading || loading || !activeEventId) return;
    if (user && isMember && flow === 'choice') {
      loadOwnProfileAndCheckIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, loading, user, isMember, activeEventId]);

  const handleSignedIn = async () => {
    await refreshAuth();
    await loadOwnProfileAndCheckIn();
  };

  const formatDate = (dateString: string) =>
    formatClubDate(dateString, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  // ---- Terminal / full-screen sub-flows -----------------------------------

  if (flow === 'signing-in') {
    return <LoginForm onSuccess={handleSignedIn} />;
  }

  if ((flow === 'checking-in' || flow === 'loading-profile') && activeEventId) {
    if (flow === 'loading-profile' || !member) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fishing-blue mx-auto" />
            <p className="text-gray-500 mt-4">Loading your membership details…</p>
          </div>
        </div>
      );
    }
    return (
      <CheckInModal
        eventId={activeEventId}
        member={member}
        familyMembers={familyMembers}
        onClose={() => setFlow('choice')}
        onSuccess={() => navigateTo('/dashboard')}
      />
    );
  }

  if (flow === 'guest' && activeEventId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4">
        <NonMemberRegistrationForm
          eventId={activeEventId}
          event={event}
          onBack={() => setFlow('choice')}
          onSuccess={() => navigateTo('/')}
        />
      </div>
    );
  }

  // ---- Main card ------------------------------------------------------------

  // #88: Event.currentAttendees is never incremented (it reads 0 for every
  // event), so the client-side capacity gate that used to read it never
  // actually reflected real capacity — a full event never stopped anyone
  // here. The real capacity check now happens server-side in
  // submit-competition-day-registration, which refuses with "This
  // competition is full." when it's genuinely full; that message is shown
  // via CheckInModal/NonMemberRegistrationForm's existing error display.
  // This page just shows the configured maximum, not a (always-0) running
  // count.

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl w-full p-8 text-center">
          <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-16 w-auto mx-auto mb-6" />

          {loading || authLoading || eventLoading ? (
            <div className="py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fishing-blue mx-auto" />
              <p className="text-gray-500 mt-4">Finding your competition…</p>
            </div>
          ) : openComps.length > 1 ? (
            <div className="text-left">
              <h1 className="text-xl font-bold text-gray-900 mb-4 text-center">Multiple Competitions Open</h1>
              <p className="text-sm text-gray-500 mb-4 text-center">Select which competition to check in to:</p>
              <div className="space-y-3">
                {openComps.map((c: any) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveEventId(c.id)}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-fishing-blue hover:shadow-md transition-all"
                  >
                    <p className="font-semibold text-gray-900">{c.title}</p>
                    <p className="text-sm text-gray-500">
                      {formatClubDate(c.eventDate, { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : activeEventId ? (
            <div className="text-left">
              {eventError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm text-center">
                  {eventError}
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">Competition Day Check-In</h1>
                  {event?.title && <h2 className="text-lg text-gray-700 mb-4 text-center">{event.title}</h2>}

                  {event && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-4 text-sm">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <span className="font-semibold text-gray-900">Date: </span>
                          <span className="text-gray-700">{formatDate(event.eventDate)}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Pricing: </span>
                          <span className="text-gray-700">Members $10 · Non-members ${event.nonMemberPrice ?? 15}</span>
                        </div>
                      </div>
                      {event.maxAttendees && (
                        <p className="mt-2 text-gray-700">Max {event.maxAttendees} attendees</p>
                      )}
                    </div>
                  )}

                  <div
                    className={`rounded-lg p-3 mb-4 text-sm ${
                      checkInWindow.isOpen ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                    }`}
                  >
                    {checkInWindow.isOpen ? 'Check-In Open — ' : 'Check-In Not Available — '}
                    {checkInWindow.message}
                  </div>

                  {checkInWindow.isOpen && (
                    <>
                      <p className="text-sm text-gray-500 mb-4 text-center">
                        Sign in to your member portal for the fastest check-in, and to track your catches + stats.
                      </p>

                      {profileError && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 mb-4">
                          {profileError}
                        </div>
                      )}

                      <button
                        onClick={() => setFlow('signing-in')}
                        className="w-full py-3 bg-fishing-blue text-white rounded-lg font-semibold shadow hover:bg-blue-700 mb-4"
                      >
                        🎣 Member Check-In
                      </button>

                      {event?.allowNonMembers !== false && (
                        <div className="pt-4 border-t border-gray-200">
                          <button
                            onClick={() => setFlow('guest')}
                            className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                          >
                            Non-Member Check-In
                          </button>
                          <p className="text-xs text-gray-500 mt-2">
                            For visitors, first-time attendees, or members who haven't set up a portal account yet
                            (or have forgotten their details — no worries, you can check in here).
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ) : nextComp ? (
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Check-in opens soon</h1>
              <p className="text-lg font-semibold text-fishing-blue mb-1">{nextComp.title}</p>
              <p className="text-sm text-gray-500 mb-4">
                {formatClubDate(nextComp.eventDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {nextComp.location ? ` · ${nextComp.location}` : ''}
              </p>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">✅ You're in the right place!</p>
                <p className="text-xs text-blue-600 mt-1">Check back on competition day when check-in opens.</p>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">No competitions scheduled</h1>
              <p className="text-sm text-gray-500">Check back soon for the next competition.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartCheckInPage;
